import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Inbox, Link2, Mail, Send, Users, X } from 'lucide-react'

import {
  useEmailThreadLinks,
  useEmailThreadsByEntity,
  useGetEmailThread,
  useMarkEmailThreadRead,
  useUnlinkEmailThread,
} from '@/stores/emails.store'
import type { EmailThreadLink } from '@/gen/cortado/v1/email_conversations_pb'
import { EmailMessageDirection, EmailThreadStatus } from '@/gen/cortado/v1/emails_pb'
import type { Email, EmailParticipant, EmailThreadLite } from '@/gen/cortado/v1/emails_pb'
import { EmailThreadLinkSourceEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatTimestamp, formatTimestampWithTime } from '@/lib/date-utils'
import { formatEnumName, getEmailThreadStatusColor } from '@/lib/enum-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { captureError } from '@/lib/sentry'
import {
  emailPersonInitials,
  emailPersonLabel,
  messagePeopleOfType,
  type EmailContactFallback,
} from './email-addresses'
import { EmailAttachmentList } from './email-attachment-list'
import { EmailBodyFrame } from './email-body-frame'
import { FailedSendIndicator } from './failed-send-retry'
import { EntityConversationLinker } from './entity-conversation-linker'
import { getRenderedThreadReadThrough } from './inbox-thread-read-state'
import { ThreadEntityPicker } from './thread-entity-picker'
import { ThreadReply } from './thread-reply'

/** Page size of the panel's list; ActivityTabs reuses it so the tab's count shares the cached first page. */
export const CONVERSATIONS_PAGE_LIMIT = 20

/** Entity types that can carry an `email_thread_links` row today. */
const LINKABLE_ENTITY_TYPES = new Set<ParentTypeEnum>([
  ParentTypeEnum.PARENT_TYPE_CONTACT,
  ParentTypeEnum.PARENT_TYPE_COMPANY,
  ParentTypeEnum.PARENT_TYPE_QUOTE,
  ParentTypeEnum.PARENT_TYPE_BOOKING,
])

interface EntityConversationsPanelProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  /** Thread to open first, e.g. from a reply notification's link. */
  initialThreadId?: string
}
/**
 * EntityConversationsPanel - per-entity view of email conversations, driven by
 * the ListEmailThreadsByEntity RPC: threads linked to the record plus those of
 * the records that roll up into it (a lead's quotes, bookings and invoices; a
 * contact's leads). It is every record's email tab for Connected inbox orgs,
 * replacing the legacy "Emails" tab (ListEmails).
 */
export function EntityConversationsPanel({
  organizationId,
  parentType,
  parentId,
  initialThreadId,
}: EntityConversationsPanelProps) {
  const [offset, setOffset] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(initialThreadId ?? null)
  // A notification followed while this record is already open changes the
  // thread without remounting, so open it here too.
  useEffect(() => {
    if (initialThreadId) setExpandedId(initialThreadId)
  }, [initialThreadId])
  const [isLinkerOpen, setIsLinkerOpen] = useState(false)

  const { threads, totalCount, isLoading, refetch } = useEmailThreadsByEntity(parentType, parentId, {
    limit: CONVERSATIONS_PAGE_LIMIT,
    offset,
    enabled: !!parentId,
  })

  const isLinkable = LINKABLE_ENTITY_TYPES.has(parentType)
  const hasLoaded = !isLoading || threads.length > 0

  return (
    <div className="space-y-3">
      {isLinkable && (
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => setIsLinkerOpen(true)}>
            <Link2 className="h-3.5 w-3.5" />
            Link a conversation
          </Button>
        </div>
      )}

      {isLoading && !hasLoaded ? (
        <ConversationsSkeleton />
      ) : threads.length === 0 ? (
        <div className="rounded-lg border border-dashed py-6 md:py-0">
          <Empty>
            <EmptyMedia variant="icon">
              <Mail className="h-5 w-5" />
            </EmptyMedia>
            <EmptyDescription>No linked conversations yet</EmptyDescription>
          </Empty>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-2">
            {threads.map((thread) => (
              <ConversationThreadRow
                key={thread.id}
                organizationId={organizationId}
                parentType={parentType}
                parentId={parentId}
                thread={thread}
                isExpanded={expandedId === thread.id}
                onToggle={() => setExpandedId((prev) => (prev === thread.id ? null : thread.id))}
              />
            ))}
          </div>

          {totalCount > CONVERSATIONS_PAGE_LIMIT && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-muted-foreground">
                {offset + 1}-{Math.min(offset + threads.length, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((o) => Math.max(0, o - CONVERSATIONS_PAGE_LIMIT))}
                  disabled={offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((o) => o + CONVERSATIONS_PAGE_LIMIT)}
                  disabled={offset + CONVERSATIONS_PAGE_LIMIT >= totalCount}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {isLinkable && (
        <EntityConversationLinker
          entityType={parentType}
          entityId={parentId}
          open={isLinkerOpen}
          onOpenChange={setIsLinkerOpen}
          onLinked={() => void refetch()}
          alreadyLinkedThreadIds={threads.map((thread) => thread.id)}
        />
      )}
    </div>
  )
}

interface ConversationThreadRowProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  thread: EmailThreadLite
  isExpanded: boolean
  onToggle: () => void
}

function ConversationThreadRow({
  organizationId,
  parentType,
  parentId,
  thread,
  isExpanded,
  onToggle,
}: ConversationThreadRowProps) {
  const isOutbound = thread.lastMessageDirection === EmailMessageDirection.OUTBOUND
  const timestamp = formatTimestampWithTime(thread.lastMessageAt)
  const participantCount = thread.participants.length
  const messageCount = thread.messageCount

  return (
    <div className="rounded-lg border transition-colors">
      <button
        type="button"
        onClick={onToggle}
        className="w-full rounded-lg p-4 text-left transition-colors hover:bg-muted/50"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              {isOutbound ? (
                <Send className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Inbox className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate text-sm font-semibold">{thread.subject || '(no subject)'}</span>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{timestamp}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={getEmailThreadStatusColor(thread.status)}>
              {formatEnumName(thread.status, EmailThreadStatus)}
            </Badge>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {participantCount} {participantCount === 1 ? 'participant' : 'participants'}
            </span>
            <span>
              {messageCount} {messageCount === 1 ? 'message' : 'messages'}
            </span>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t p-3">
          <ConversationThreadDetail
            organizationId={organizationId}
            threadId={thread.id}
            replyParent={{ parentType, parentId }}
            contact={{ name: thread.contactName }}
          />
        </div>
      )}
    </div>
  )
}

interface ConversationThreadDetailProps {
  organizationId: string
  threadId: string
  /** Record a reply files under; null hides Reply and shows `noReplyNote`. */
  replyParent: { parentType: ParentTypeEnum; parentId: string } | null
  noReplyNote?: string
  /**
   * Hides the "Linked to" row and the "which event" picker, for surfaces that
   * already sit on one record (the job page's Activity card).
   */
  hideLinks?: boolean
  /** The thread's contact, which names addresses nothing else names. */
  contact?: EmailContactFallback
}

/**
 * One open conversation: its links, the "which event" picker, every message
 * and the Reply action. Rendering it marks the thread read through the last
 * message shown. Shared by every record's Conversations tab and the Inbox.
 */
export function ConversationThreadDetail({
  organizationId,
  threadId,
  replyParent,
  noReplyNote,
  hideLinks = false,
  contact,
}: ConversationThreadDetailProps) {
  const { markRead, isPending: isMarkingRead } = useMarkEmailThreadRead()
  const { links, isLoading: isLoadingLinks } = useEmailThreadLinks(threadId, { enabled: !hideLinks })
  const { thread: threadDetail, isLoading: isLoadingThread } = useGetEmailThread(threadId)
  const markedReadThroughRef = useRef<string | null>(null)

  const messages = threadDetail?.messages ?? []

  useEffect(() => {
    if (!threadDetail || isMarkingRead) return
    const readThrough = getRenderedThreadReadThrough(threadDetail, messages)
    if (!readThrough) return
    const marker = `${threadId}:${readThrough.seconds}:${readThrough.nanos}`
    if (markedReadThroughRef.current === marker) return
    markedReadThroughRef.current = marker
    void markRead(organizationId, threadId, readThrough)
  }, [isMarkingRead, markRead, messages, organizationId, threadId, threadDetail])

  return (
    <div className="space-y-3">
      {!hideLinks && (
        <>
          <ThreadLinksRow organizationId={organizationId} threadId={threadId} links={links} isLoading={isLoadingLinks} />
          <ThreadEntityPicker
            organizationId={organizationId}
            threadId={threadId}
            reviewReason={threadDetail?.reviewReason}
          />
        </>
      )}
      {isLoadingThread && !threadDetail ? (
        <ConversationsSkeleton />
      ) : (
        <div className="divide-y">
          {messages.map((message) => (
            <ConversationMessageRow
              key={message.id}
              organizationId={organizationId}
              email={message}
              participants={threadDetail?.participants ?? []}
              contact={contact}
            />
          ))}
        </div>
      )}
      {replyParent ? (
        <ThreadReply
          organizationId={organizationId}
          parentType={replyParent.parentType}
          parentId={replyParent.parentId}
          threadId={threadId}
        />
      ) : (
        noReplyNote && <p className="text-xs text-muted-foreground">{noReplyNote}</p>
      )}
    </div>
  )
}

interface ThreadLinksRowProps {
  organizationId: string
  threadId: string
  links: EmailThreadLink[]
  isLoading: boolean
}

/**
 * Shows every CRM entity this conversation is linked to. Manual links (a
 * human decision) render solid with a prominent unlink action; auto-matched
 * and send-context links render muted, so the two are never confused.
 */
function ThreadLinksRow({ organizationId, threadId, links, isLoading }: ThreadLinksRowProps) {
  const { unlink, isLoading: isUnlinking } = useUnlinkEmailThread()
  const [unlinkingKey, setUnlinkingKey] = useState<string | null>(null)

  if (isLoading && links.length === 0) {
    return <Skeleton className="h-6 w-40" />
  }
  if (links.length === 0) {
    return null
  }

  const handleUnlink = async (link: EmailThreadLink) => {
    const key = `${link.entityType}:${link.entityId}`
    setUnlinkingKey(key)
    try {
      await unlink({ threadId, entityType: link.entityType, entityId: link.entityId, organizationId })
      toast.success('Conversation unlinked')
    } catch (error) {
      reportError(error, { feature: 'email_unlink_conversation', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'entity-conversations-panel',
      })
      toast.error('Failed to unlink conversation', {
        description: getErrorDescription(error),
      })
    } finally {
      setUnlinkingKey(null)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">Linked to:</span>
      {links.map((link) => {
        const key = `${link.entityType}:${link.entityId}`
        const isManual = link.linkSource === EmailThreadLinkSourceEnum.EMAIL_THREAD_LINK_SOURCE_MANUAL
        const sourceTag =
          link.linkSource === EmailThreadLinkSourceEnum.EMAIL_THREAD_LINK_SOURCE_AUTO_MATCH
            ? 'auto'
            : link.linkSource === EmailThreadLinkSourceEnum.EMAIL_THREAD_LINK_SOURCE_SEND_CONTEXT
              ? 'send'
              : null
        // entity_label is best-effort and unset when the server couldn't
        // resolve the entity — fall back rather than showing an empty row.
        const label = link.entityLabel?.trim() || `${link.entityId.slice(0, 8)}…`

        return (
          <span
            key={key}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              isManual ? 'border-foreground/20 bg-muted' : 'border-dashed text-muted-foreground',
            )}
          >
            <span className="font-medium">{formatEnumName(link.entityType, ParentTypeEnum)}</span>
            <span className="max-w-[10rem] truncate">{label}</span>
            {sourceTag && <span className="text-[10px] uppercase tracking-wide">{sourceTag}</span>}
            <button
              type="button"
              onClick={() => void handleUnlink(link)}
              disabled={isUnlinking && unlinkingKey === key}
              title={isManual ? 'Unlink' : 'Unlink (added automatically)'}
              aria-label={`Unlink ${label}`}
              className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )
      })}
    </div>
  )
}

interface ConversationMessageRowProps {
  organizationId: string
  email: Email
  /** The thread's participants, which name addresses the message left unnamed. */
  participants: readonly EmailParticipant[]
  /** The thread's contact, the last name source before the bare address. */
  contact?: EmailContactFallback
}

/**
 * One message of an open conversation: who sent it (name and address, with an
 * initials avatar coloured by side), who it went to, when, its body and its
 * attachments. A message stored without a From address falls back to its side.
 */
function ConversationMessageRow({ organizationId, email, participants, contact }: ConversationMessageRowProps) {
  const isOutbound = email.direction === EmailMessageDirection.OUTBOUND
  const sender = messagePeopleOfType(email.addresses, 'From', participants, contact)[0]
  const to = messagePeopleOfType(email.addresses, 'To', participants, contact)
  const cc = messagePeopleOfType(email.addresses, 'Cc', participants, contact)
  const senderLabel = sender ? emailPersonLabel(sender) : isOutbound ? 'You' : 'Customer'
  const hasBody = !!email.body

  return (
    <article className="flex gap-3 py-4 first:pt-0 last:pb-0" aria-label={`Message from ${senderLabel}`}>
      <Avatar className="mt-0.5 size-8">
        <AvatarFallback
          className={cn(
            'text-xs font-semibold',
            isOutbound ? 'bg-brand text-brand-foreground' : 'bg-foreground text-background',
          )}
        >
          {sender ? emailPersonInitials(sender) : isOutbound ? <Send className="h-3.5 w-3.5" /> : <Inbox className="h-3.5 w-3.5" />}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 text-sm">
            <p className="truncate">
              <span className="font-semibold">{senderLabel}</span>
              {sender?.name && <span className="text-muted-foreground"> &lt;{sender.email}&gt;</span>}
            </p>
            {(to.length > 0 || cc.length > 0) && (
              <p className="truncate text-xs text-muted-foreground">
                {to.length > 0 && `to ${to.map(emailPersonLabel).join(', ')}`}
                {to.length > 0 && cc.length > 0 && ', '}
                {cc.length > 0 && `cc ${cc.map(emailPersonLabel).join(', ')}`}
              </p>
            )}
          </div>
          <time className="shrink-0 text-xs text-muted-foreground">{formatTimestamp(email.createdAt, 'MMM d, h:mm a')}</time>
        </div>
        {isOutbound && (
          <FailedSendIndicator
            emailId={email.id}
            organizationId={organizationId}
            deliveryStatus={email.deliveryStatus}
            bounceReason={email.bounceReason}
            lastSendError={email.lastSendError}
            sendAttempts={email.sendAttempts}
          />
        )}
        {hasBody ? (
          <EmailBodyFrame html={email.body!} />
        ) : (
          <p className="text-sm italic text-muted-foreground">(no content)</p>
        )}
        <EmailAttachmentList email={email} />
      </div>
    </article>
  )
}

function ConversationsSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      ))}
    </div>
  )
}
